<template>
  <article class="portal-analytics-card">
    <header class="portal-analytics-card__header">
      <h3 class="portal-analytics-card__title">{{ $t(config.titleKey) }}</h3>
      <div class="portal-analytics-card__view-toggle" role="group">
        <button
          type="button"
          class="portal-analytics-card__view-btn"
          :class="{ 'portal-analytics-card__view-btn--active': viewMode === 'chart' }"
          @click="viewMode = 'chart'"
        >
          {{ $t("portalHome.analytics.view.chart") }}
        </button>
        <button
          type="button"
          class="portal-analytics-card__view-btn"
          :class="{ 'portal-analytics-card__view-btn--active': viewMode === 'table' }"
          @click="viewMode = 'table'"
        >
          {{ $t("portalHome.analytics.view.table") }}
        </button>
      </div>
    </header>

    <div class="portal-analytics-card__body">
      <div v-show="viewMode === 'chart'" class="portal-analytics-card__chart-wrap">
        <Bar
          :key="selectedTireType"
          :chart-data="chartData"
          :chart-options="chartOptions"
          :plugins="barChartPlugins"
          :style="{ height: '280px', width: '100%' }"
        />
      </div>
      <div
        v-show="viewMode === 'table'"
        class="portal-analytics-card__table-wrap"
        :style="tableAccentStyle"
      >
        <table class="portal-analytics-card__table">
          <thead>
            <tr>
              <th
                scope="col"
                class="portal-analytics-card__th portal-analytics-card__th--label"
              >
                {{ $t("portalHome.analytics.table.month") }}
              </th>
              <th
                scope="col"
                class="portal-analytics-card__th portal-analytics-card__th--value"
              >
                <span class="portal-analytics-card__th-inner">
                  <span
                    class="portal-analytics-card__legend-dot"
                    style="background-color: #c9c9c9"
                    aria-hidden="true"
                  />
                  {{ $t("portalHome.analytics.legend.lastYear") }}
                </span>
              </th>
              <th
                scope="col"
                class="portal-analytics-card__th portal-analytics-card__th--value"
              >
                <span class="portal-analytics-card__th-inner">
                  <span
                    class="portal-analytics-card__legend-dot"
                    :style="{ backgroundColor: config.thisYearColor }"
                    aria-hidden="true"
                  />
                  {{ $t("portalHome.analytics.legend.thisYear") }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(monthKey, index) in monthKeys"
              :key="monthKey"
              class="portal-analytics-card__row"
            >
              <th
                scope="row"
                class="portal-analytics-card__cell portal-analytics-card__cell--label"
              >
                {{ $t(`portalHome.analytics.months.${monthKey}`) }}
              </th>
              <td class="portal-analytics-card__cell portal-analytics-card__cell--value">
                <span class="portal-analytics-card__value">
                  {{ formatTableValue(activeSeries.lastYear[index]) }}
                </span>
              </td>
              <td
                class="portal-analytics-card__cell portal-analytics-card__cell--value portal-analytics-card__cell--this-year"
              >
                <span class="portal-analytics-card__value portal-analytics-card__value--accent">
                  {{ formatTableValue(activeSeries.thisYear[index]) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="portal-analytics-card__footer portal-analytics-card__footer--bar">
      <div class="portal-analytics-card__footer-side portal-analytics-card__footer-side--start">
        <PortalAnalyticsTireSelect
          v-model="selectedTireType"
          :select-id="filterId"
        />
      </div>
      <ul v-show="viewMode === 'chart'" class="portal-analytics-card__bar-legend">
        <li>
          <span
            class="portal-analytics-card__legend-dot"
            style="background-color: #c9c9c9"
          />
          <span>{{ $t("portalHome.analytics.legend.lastYear") }}</span>
        </li>
        <li>
          <span
            class="portal-analytics-card__legend-dot"
            :style="{ backgroundColor: config.thisYearColor }"
          />
          <span>{{ $t("portalHome.analytics.legend.thisYear") }}</span>
        </li>
      </ul>
      <div class="portal-analytics-card__footer-side portal-analytics-card__footer-side--end">
        <PortalAnalyticsDownloadButton
          class="portal-analytics-card__download"
          :export-type="config.exportType"
        />
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed, defineProps, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
} from "chart.js";
import {
  PORTAL_ANALYTICS_MONTH_KEYS,
  getPortalAnalyticsBarSeries,
} from "@/data/portal_analytics_mock";
import PortalAnalyticsTireSelect from "@/components/PortalHome/Analytics/PortalAnalyticsTireSelect.vue";
import PortalAnalyticsDownloadButton from "@/components/PortalHome/Analytics/PortalAnalyticsDownloadButton.vue";
import {
  createPortalAnalyticsBarValueLabelsPlugin,
  portalAnalyticsChartOptions,
  portalAnalyticsFont,
} from "@/components/PortalHome/Analytics/portalAnalyticsCharts.js";

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend);

const BAR_WIDTH_PX = 12;
const BAR_GAP_PX = 14;
const BAR_SLOT_PX = BAR_WIDTH_PX + BAR_GAP_PX;
const barBorderRadius = BAR_WIDTH_PX / 2;
const BAR_VALUE_LABEL_OFFSET_PX = 8;
const BAR_VALUE_LABEL_TOP_INSET_PX = 4;
const BAR_VALUE_LABEL_FONT_SIZE_PX = 16;

const props = defineProps({
  config: { type: Object, required: true },
});

const { t, locale } = useI18n();

function formatTableValue(value) {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return "—";
  }

  return numeric.toLocaleString(locale.value);
}
const viewMode = ref("chart");
const selectedTireType = ref("all");
const monthKeys = PORTAL_ANALYTICS_MONTH_KEYS;
const filterId = computed(() => `portal-analytics-filter-${props.config.id}`);
const tableAccentStyle = computed(() => ({
  "--portal-analytics-table-accent": props.config.thisYearColor,
}));

const activeSeries = computed(() =>
  getPortalAnalyticsBarSeries(props.config, selectedTireType.value),
);

const monthLabels = computed(() =>
  monthKeys.map((key) => t(`portalHome.analytics.months.${key}`))
);

const chartData = computed(() => ({
  labels: monthLabels.value,
  datasets: [
    {
      label: t("portalHome.analytics.legend.lastYear"),
      data: activeSeries.value.lastYear,
      backgroundColor: "#c9c9c9",
      borderRadius: barBorderRadius,
      borderSkipped: false,
      barThickness: BAR_SLOT_PX,
      maxBarThickness: BAR_WIDTH_PX,
    },
    {
      label: t("portalHome.analytics.legend.thisYear"),
      data: activeSeries.value.thisYear,
      backgroundColor: props.config.thisYearColor,
      borderRadius: barBorderRadius,
      borderSkipped: false,
      barThickness: BAR_SLOT_PX,
      maxBarThickness: BAR_WIDTH_PX,
    },
  ],
}));

const barChartPlugins = [
  createPortalAnalyticsBarValueLabelsPlugin({
    offsetPx: BAR_VALUE_LABEL_OFFSET_PX,
    topInsetPx: BAR_VALUE_LABEL_TOP_INSET_PX,
    fontSizePx: BAR_VALUE_LABEL_FONT_SIZE_PX,
  }),
];

const chartOptions = computed(() =>
  portalAnalyticsChartOptions({
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      x: {
        border: { display: false },
        grid: { display: false, drawBorder: false },
        ticks: {
          color: "#636466",
          font: portalAnalyticsFont(14),
          maxRotation: 0,
          autoSkip: false,
        },
      },
      y: {
        display: false,
        border: { display: false },
        grid: { display: false, drawBorder: false },
      },
    },
    layout: { padding: { top: 8, left: 36, right: 36, bottom: 4 } },
  }),
);

</script>

<style scoped lang="scss">
@import "@/styles/portal-home/portal-analytics";
</style>
